import React, { useState } from "react";
import { Button, Input, Modal, Progress, Radio, Upload, App } from "antd";
import { CloseOutlined } from "@ant-design/icons";
import GraduationCapIcon from "../GraduationCapIcon";
import { TrainingRow } from "../../../../services/formacion/formacion.types";
import {
  setTrainingRecording,
  prepareRecordingUpload,
  updateTraining,
} from "../../../../services/formacion/formacion.service";
import { uploadFileToSignedUrl } from "../../../../utils/uploadFileToSignedUrl";
import { getApiErrorMessage } from "../../../../utils";
import CoverUploadInput from "../TrainingModal/CoverUploadInput";
import styles from "../TrainingModal/styles.module.scss";

const ACCEPTED_VIDEO_TYPES = ["video/mp4", "video/webm", "video/quicktime"];
const ACCEPTED_VIDEO_EXTENSIONS = [".mp4", ".webm", ".mov"];

const isAcceptedVideoFile = (file: File): boolean =>
  ACCEPTED_VIDEO_TYPES.includes(file.type) ||
  ACCEPTED_VIDEO_EXTENSIONS.some((ext) => file.name.toLowerCase().endsWith(ext));

const isValidUrl = (value: string): boolean => {
  try {
    const parsed = new URL(value);
    return parsed.protocol === "http:" || parsed.protocol === "https:";
  } catch {
    return false;
  }
};

interface Props {
  open: boolean;
  schoolId: number | null;
  training: TrainingRow;
  onClose: () => void;
  onSaved: () => void;
}

type Mode = "upload" | "link";

const RecordingUploadModal: React.FC<Props> = ({
  open,
  schoolId,
  training,
  onClose,
  onSaved,
}) => {
  const { message, notification } = App.useApp();
  const [mode, setMode] = useState<Mode>("upload");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadedUrl, setUploadedUrl] = useState<string | undefined>();
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [abortController, setAbortController] =
    useState<AbortController | null>(null);
  const [linkValue, setLinkValue] = useState("");
  const [coverUrl, setCoverUrl] = useState<string | null>(
    training.cover_image_url
  );
  const [submitting, setSubmitting] = useState(false);

  const startUpload = async (file: File) => {
    setUploading(true);
    setUploadProgress(0);
    const controller = new AbortController();
    setAbortController(controller);
    try {
      const { signedUrl, url } = await prepareRecordingUpload(
        schoolId,
        training.id,
        file.type
      );
      await uploadFileToSignedUrl(signedUrl, file, setUploadProgress, {
        signal: controller.signal,
      });
      setUploadedUrl(url);
    } catch (error: any) {
      if (error?.name !== "AbortError") {
        message.error("Error al subir la grabación");
      }
      setSelectedFile(null);
    } finally {
      setUploading(false);
      setAbortController(null);
    }
  };

  const handleFileSelect = (file: File) => {
    if (!isAcceptedVideoFile(file)) {
      message.error("Formato no soportado. Usá MP4, WEBM o MOV.");
      return false;
    }
    setSelectedFile(file);
    setUploadedUrl(undefined);
    startUpload(file);
    return false;
  };

  const handleCancelUpload = () => {
    abortController?.abort();
  };

  const canConfirm =
    mode === "upload" ? !!uploadedUrl && !uploading : isValidUrl(linkValue);

  const handleConfirm = async () => {
    if (!canConfirm) return;
    setSubmitting(true);
    try {
      const recordingUrl = mode === "upload" ? uploadedUrl! : linkValue;
      await setTrainingRecording(schoolId, training.id, {
        recording_url: recordingUrl,
      });
      if (coverUrl !== training.cover_image_url) {
        await updateTraining(schoolId, training.id, {
          cover_image_url: coverUrl,
        });
      }
      notification.success({
        message: "Éxito",
        description: "La grabación fue guardada exitosamente!",
        placement: "top",
        duration: 3,
      });
      onSaved();
    } catch (error: any) {
      notification.error({
        message: "Error",
        description: getApiErrorMessage(
          error,
          "La grabación no pudo ser guardada."
        ),
        placement: "top",
        duration: 4,
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      width={520}
      closeIcon={<CloseOutlined className={styles.closeIcon} />}
      title={
        <div className={styles.header}>
          <GraduationCapIcon className={styles.headerIcon} />
          <span>Subir grabación</span>
        </div>
      }
    >
      <Radio.Group
        value={mode}
        onChange={(event) => setMode(event.target.value)}
        style={{ marginBottom: 16 }}
      >
        <Radio.Button value="upload">Subir video</Radio.Button>
        <Radio.Button value="link">Pegar enlace</Radio.Button>
      </Radio.Group>

      {mode === "upload" ? (
        <div style={{ marginBottom: 16 }}>
          <Upload
            beforeUpload={handleFileSelect}
            showUploadList={false}
            accept="video/mp4,video/webm,video/quicktime,.mp4,.webm,.mov"
            disabled={uploading}
          >
            <Button disabled={uploading}>Elegir archivo</Button>
          </Upload>
          {selectedFile && (
            <div style={{ marginTop: 12 }}>
              <div>{selectedFile.name}</div>
              <Progress percent={uploadProgress} />
              {uploading && (
                <Button danger size="small" onClick={handleCancelUpload}>
                  Cancelar subida
                </Button>
              )}
              {uploadedUrl && !uploading && (
                <div className={styles.emptyState}>Video subido</div>
              )}
            </div>
          )}
        </div>
      ) : (
        <Input
          placeholder="https://..."
          value={linkValue}
          onChange={(event) => setLinkValue(event.target.value)}
          style={{ marginBottom: 16 }}
        />
      )}

      <div style={{ marginBottom: 16 }}>
        <div style={{ marginBottom: 8, fontWeight: 500 }}>Portada</div>
        <CoverUploadInput
          schoolId={schoolId}
          trainingId={training.id}
          value={coverUrl}
          onChange={setCoverUrl}
        />
      </div>

      <div className={styles.footer}>
        <Button onClick={onClose}>Cancelar</Button>
        <Button
          type="primary"
          className={styles.ctaButton}
          loading={submitting}
          disabled={!canConfirm}
          onClick={handleConfirm}
        >
          Guardar
        </Button>
      </div>
    </Modal>
  );
};

export default RecordingUploadModal;
